import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TextButton } from '@safetyhub/ui';
import { useAuthStore } from '../stores/auth';
import { useEntityChanged } from '../hooks/useEntityChanged';
import { fetchPinnedAnnouncements, type AnnouncementBoardItem } from '../lib/announcements';
import { BANNER_LAYER } from './modalLayers';

interface AnnouncementBandProps {
  /** 위쪽 배너들의 높이 합 — 겹치지 않게 그만큼 내려서 붙는다. */
  topOffset?: number;
  /** 실제 렌더 높이 보고 — Layout이 본문 오프셋을 이 값으로 쌓는다. */
  onHeightChange?: (height: number) => void;
}

/**
 * 공지 **고정 띠** — 줄기 `popup-and-announcement-surface` P4-b.
 *
 * ## 왜 배너 스택의 구성원인가 (팝업이 아니고, 게시판 안도 아니다)
 *
 * 결정 `2026-10-15-release-note-surfaces-as-a-set-and-popup-decisions` §B-ⓓ가 **「전사는 팝업,
 * 조직은 고정 띠」**로 정했고, 줄기가 그 근거를 「**배너 스택 기제가 이미 있어 고정 띠가 싸다**」로
 * 적는다. 띠는 **노출** 축이라 게시판 페이지 안에만 두면 **그 페이지에 들어온 사람만 보게 되어
 * 노출이 아니게 된다** — 보존 축은 `/announcements`(게시판)가 소유한다.
 *
 * ⚠️ **그 「상한 2」가 세는 분모는 「상시 배너 자격」이고 띠는 그 밖이다** — 띠와 「확인이 필요한
 * 항목」 바가 동시에 떠도 상한을 넘지 않는다(그 판정과 근거·기각한 두 처방은 짝 spec
 * `announcementBand.test.tsx`의 **「[판정: 상한 2의 분모] …」 케이스**가 정본이다. 여기 베껴 적지
 * 않는다 — 두 자리에 적으면 한쪽이 낡는다).
 *
 * ⚠️ **새 슬롯을 만들지 않는다** — 상한·접힘은 결정
 * `2026-08-19-banner-slot-policy-and-actionable-items` 3항의 규약을 그대로 쓴다(서버는 후보만
 * 돌려주고 판정은 화면 몫이다 — `listPinnedBanners` JSDoc이 「**`take: 2`로 좁히지 말 것**」을
 * 그래서 적는다):
 *
 *  - **0건**: 아무것도 렌더하지 않는다(높이 0 보고).
 *  - **1건**: 접지 않고 **그대로 배너처럼** — 제목 + 「공지 보기」가 직접 보인다.
 *  - **2건+**: 접힌 한 줄(`공지 N건`) + **그 자리에서** 펼친다.
 *
 * `topOffset`/`onHeightChange` 관례는 `ActionItemsBar`와 같다(새 관례를 만들지 않는다 — 고정 높이
 * 가정이 두 번 사고를 냈다. 가드: `__tests__/bannerStackMeasuredHeight.test.ts`).
 *
 * ## 「확인이 필요한 항목」에 넣지 **않는** 이유
 *
 * 항목은 **조치해야 사라지는 것**이고(결정 2026-08-19 §4 표) 공지는 조치 대상이 아니다. 서버도
 * 띠 후보를 **봤음으로 거르지 않는다** — 「고정」의 뜻이 「계속 보인다」이기 때문이다
 * (`listPinnedBanners` JSDoc). 항목 store에 섞으면 바 문구 「확인이 필요한 항목 N건」이 공지에
 * 거짓이 되고, 같은 자리에서 두 축이 한 숫자로 합쳐진다.
 *
 * ⚠️ 이 컴포넌트의 spec은 **실제 픽셀을 재지 못한다**(jsdom에 레이아웃이 없다 — `offsetHeight`는
 * 늘 0이다). 재는 것은 **배선의 형태와 접힘 규약**뿐이고, 겹침·틈은 dev 실측의 몫이다.
 */
export default function AnnouncementBand({
  topOffset = 0,
  onHeightChange,
}: AnnouncementBandProps) {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const [items, setItems] = useState<AnnouncementBoardItem[]>([]);
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  /**
   * 조회 회차 — **마지막 요청의 응답만** 반영한다.
   *
   * 종전에는 effect 안의 `alive` 플래그였고 그것이 막는 것은 「`userId`가 바뀌거나 언마운트된
   * 뒤의 늦은 응답」뿐이었다. 실시간 재조회가 붙은 뒤로는 **같은 `userId`에서 조회가 겹칠 수**
   * 있어(마운트 조회 ↔ `ENTITY_CHANGED` 재조회, 또는 재조회 둘) 회차로 가른다 — 먼저 보낸
   * 요청이 늦게 도착해 **새 목록을 옛 목록으로 덮는** 자리를 닫는다.
   */
  const requestRef = useRef(0);

  /**
   * 띠 후보 재조회 — **스피너가 없다**(이 컴포넌트는 로딩 상태를 그리지 않는다). 응답이 올
   * 때까지 `items`가 그대로라 재조회가 띠를 깜빡이게 하지 않으므로, 선례들의 `{ silent: true }`
   * 플래그에 해당하는 것이 **구조적으로 이미 그렇다**.
   */
  const loadBanners = useCallback(async () => {
    const seq = ++requestRef.current;
    if (!userId) {
      setItems([]);
      return;
    }
    try {
      const rows = await fetchPinnedAnnouncements();
      if (requestRef.current === seq) setItems(rows);
    } catch {
      // 띠는 **보조 노출**이다 — 실패를 토스트로 알리면 접속마다 떠서 소음이 된다.
      // 보존 축(`/announcements`)이 같은 데이터를 다시 청하고 그 화면은 실패를 보여준다.
      if (requestRef.current === seq) setItems([]);
    }
  }, [userId]);

  useEffect(() => {
    void loadBanners();
  }, [loadBanners]);

  /**
   * 공지 쓰기 뒤 즉시 반영 — 서버 `ENTITY_CHANGED(announcement)`(줄기 P9, 2026-10-27).
   *
   * ⚠️ **`paused`를 주지 않는다** — 이 컴포넌트에는 덮어쓸 입력이 없다(폼·편집 상태 0). 유일한
   * 로컬 상태 `expanded`는 재조회가 건드리지 않고, 1건으로 줄 때 되돌리는 것은 아래 effect가
   * 소유하는 **의도된** 동작이다. 입력을 가진 화면(`pages/settings/Announcements`)은 `paused`를 준다.
   *
   * ⚠️ **전사 공지는 이 이벤트로 오지 않는다** — 그쪽은 `companyId`가 없어 조직 채널을 쓸 수
   * 없다(서버 `AnnouncementAuthoringService.emitChanged` JSDoc이 정본). 즉 띠가 즉시 갱신되는
   * 것은 **조직 공지**이고, 전사 공지는 다음 진입·새로고침에 나타난다.
   */
  useEntityChanged(['announcement'], () => void loadBanners());

  const shouldRender = items.length > 0;
  const collapsible = items.length > 1;

  useEffect(() => {
    if (!shouldRender) onHeightChange?.(0);
  }, [shouldRender, onHeightChange]);

  // 1건으로 줄면 접힘 개념이 없어지므로 펼침 상태를 되돌린다(다시 2건이 될 때 지난 펼침이
  // 되살아나지 않게) — `ActionItemsBar`와 같은 규약.
  useEffect(() => {
    if (!collapsible) setExpanded(false);
  }, [collapsible]);

  useLayoutEffect(() => {
    if (!shouldRender || !ref.current) return;
    const element = ref.current;
    const update = () => onHeightChange?.(element.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldRender, onHeightChange]);

  if (!shouldRender) return null;

  /**
   * z는 **「확인이 필요한 항목」 바 아래**다 — 공지 띠는 차단성도 조치 대상도 아니라 헤더·기관 인증·
   * 항목 바보다 낮아야 한다. 값과 순서의 정본은 `modalLayers`의 `BANNER_LAYER`다.
   */
  return (
    <div
      ref={ref}
      className={`fixed left-0 right-0 ${BANNER_LAYER.announcement} border-b border-brand-200 bg-brand-50`}
      data-testid="announcement-band"
      style={{ top: topOffset }}
    >
      <div className="mx-auto max-w-7xl px-4 py-2.5 sm:px-6 lg:px-8">
        {collapsible ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-brand-700">
                공지 {items.length}건
              </p>
              <TextButton
                className="flex-shrink-0 text-xs"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? '접기' : '펼치기'}
              </TextButton>
            </div>
            {expanded && (
              <ul className="mt-2 space-y-2 border-t border-brand-200 pt-2">
                {items.map((item) => (
                  <li key={item.id} className="flex flex-wrap items-center justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-brand-700">
                      {item.title}
                    </span>
                    <Link
                      to="/announcements"
                      className="flex-shrink-0 text-xs font-medium text-brand-700 underline"
                    >
                      공지 보기
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="min-w-0 flex-1 truncate text-sm font-medium text-brand-700">
              {items[0].title}
            </p>
            <Link
              to="/announcements"
              className="flex-shrink-0 text-xs font-medium text-brand-700 underline"
            >
              공지 보기
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
